import React, { useEffect, createContext, useContext, useReducer } from 'react';
import { Product, ProductAddOn, ProductColor } from '../data/products';
export interface CartItem {
  id: string; // Unique ID for the cart line item (product.id + selected options)
  productId: string;
  product: Product;
  quantity: number;
  selectedSize?: string;
  selectedColor?: ProductColor;
  selectedAddOns: ProductAddOn[];
  unitPrice: number; // Computed price including add-ons and sale
}
interface CartState {
  items: CartItem[];
  isOpen: boolean;
}
type CartAction = {
  type: 'ADD_ITEM';
  payload: CartItem;
} | {
  type: 'REMOVE_ITEM';
  payload: string;
} | {
  type: 'UPDATE_QUANTITY';
  payload: {
    id: string;
    quantity: number;
  };
} | {
  type: 'TOGGLE_CART';
  payload?: boolean;
} | {
  type: 'CLEAR_CART';
};
const initialState: CartState = {
  items: [],
  isOpen: false
};
const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
  cartTotal: number;
  itemCount: number;
} | undefined>(undefined);
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM':
      {
        const existingItemIndex = state.items.findIndex(item => item.id === action.payload.id);
        if (existingItemIndex >= 0) {
          const newItems = [...state.items];
          newItems[existingItemIndex].quantity += action.payload.quantity;
          return {
            ...state,
            items: newItems,
            isOpen: true
          };
        }
        return {
          ...state,
          items: [...state.items, action.payload],
          isOpen: true
        };
      }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item => item.id === action.payload.id ? {
          ...item,
          quantity: action.payload.quantity
        } : item)
      };
    case 'TOGGLE_CART':
      return {
        ...state,
        isOpen: action.payload !== undefined ? action.payload : !state.isOpen
      };
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    default:
      return state;
  }
}
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(cartReducer, initialState, initial => {
    try {
      const localData = localStorage.getItem('beachShopCart');
      return localData ? {
        ...initial,
        items: JSON.parse(localData)
      } : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    localStorage.setItem('beachShopCart', JSON.stringify(state.items));
  }, [state.items]);
  const cartTotal = state.items.reduce((total, item) => total + item.unitPrice * item.quantity, 0);
  const itemCount = state.items.reduce((count, item) => count + item.quantity, 0);
  return <CartContext.Provider value={{
    state,
    dispatch,
    cartTotal,
    itemCount
  }} data-id="element-199">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}